/**
 * SOVRANOVA — ConsentFlow (Ambient Companion, Wave 2: the consent screen)
 * ------------------------------------------------------------
 * The pre-capture, all-party consent flow from docs/ambient-companion-spec.md §3:
 *   1. name every participant about to be recorded,
 *   2. show the versioned consent notice (what / where / how to delete),
 *   3. each named person gives an explicit "I consent" tap,
 * then records it via POST /api/ambient/consent (ambient_consents +
 * append-only audit). Consent is per-session and revocable here.
 *
 * This screen enables NO recording. Third-party capture (Waves 3-4) does not
 * ship until this flow is wired to capture AND an AU privacy lawyer signs off
 * (spec §12). Gated by VITE_FEATURE_AMBIENT_CONSENT (default OFF).
 */
import { useState, useEffect } from 'react'
import { api } from '../../services/api'
// Shared draw-to-sign pad (extracted 19 Aug so the couples-session agreement
// uses the identical pad). tone='dark' preserves this screen's look exactly.
import { SignaturePad } from '../common/SignaturePad'

const ROLES = [
  { value: 'therapist', label: 'Therapist' },
  { value: 'partner',   label: 'Partner' },
  { value: 'other',     label: 'Other' },
]

// Shown if the notice fetch fails — same terms, so the screen still informs.
const FALLBACK_NOTICE = {
  version: null,
  title: 'Recording consent',
  intro: 'SOVRANOVA never records a conversation involving anyone other than the account holder until every participant has given informed consent, before recording starts.',
  points: [
    'What is recorded: the audio of this conversation, from when recording starts until it stops.',
    'What is made from it: a transcript, a summary, and emotional themes — visible only to the account holder.',
    // Final beta RC, 4 Sep 2026: "Never shared, never used for ads, never used
    // to train AI models" was a beta-visible absolute this product cannot
    // honour — content IS processed by third-party providers (privacy.html).
    // Kept in step with the server notice in services/ambient/ConsentService.js;
    // if you change one, change both.
    'Where it lives: stored encrypted in the account holder’s private space, readable only by their account. To produce the transcript, summary and themes, the content is processed by the third-party service providers that help operate SOVRANOVA. SOVRANOVA does not sell your information and does not use it for advertising. Our Privacy Policy lists the providers involved and how they handle data.',
    'Your rights: any participant can withdraw consent at any time — recording stops and that recording is scheduled for deletion.',
  ],
  affirmation: 'I understand what will be recorded and how it will be used, and I consent to being recorded.',
}

export function ConsentFlow({ onBack, onBeginCapture }) {
  const [step, setStep]           = useState('participants') // participants → notice → consent → done
  const [notice, setNotice]       = useState(null)
  const [captureOn, setCaptureOn] = useState(false) // server-decided, per-account
  const [people, setPeople]       = useState([{ name: '', role: 'therapist', consented: false, signature: null }])
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState(null)
  const [recorded, setRecorded]   = useState(null) // { consentId, allParty }
  const [revoked, setRevoked]     = useState(false)
  const [register, setRegister]   = useState([])   // past consent records (view + revoke)
  const [revokingId, setRevokingId] = useState(null)

  useEffect(() => {
    let alive = true
    api.get('/ambient/consent/notice')
      .then(n => { if (alive) setNotice(n) })
      .catch(() => { if (alive) setNotice(FALLBACK_NOTICE) })
    api.get('/ambient/consents')
      .then(r => { if (alive && Array.isArray(r?.consents)) setRegister(r.consents) })
      .catch(() => {})
    // Whether THIS account may proceed from consent to an actual capture is the
    // server's call (feature flag + allowlist) — the UI only ever reflects it.
    api.get('/ambient/capture/availability')
      .then(r => { if (alive) setCaptureOn(r?.enabled === true) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  const setPerson = (i, patch) =>
    setPeople(ps => ps.map((p, j) => (j === i ? { ...p, ...patch } : p)))
  const addPerson = () =>
    setPeople(ps => [...ps, { name: '', role: 'other', consented: false, signature: null }])
  const removePerson = (i) =>
    setPeople(ps => ps.filter((_, j) => j !== i))

  const named    = people.filter(p => p.name.trim().length > 0)
  const allNamed = people.length > 0 && named.length === people.length
  const allConsented = named.length > 0 && named.every(p => p.consented)

  const submit = async () => {
    if (!allConsented || saving) return
    setError(null); setSaving(true)
    try {
      const r = await api.post('/ambient/consent', {
        method: 'in_app_signature',
        participants: named.map(p => ({
          name: p.name.trim(), role: p.role, method: 'in_app_signature', consented: true,
          signature: p.signature || null,
        })),
      })
      setRecorded(r)
      setStep('done')
    } catch (err) {
      setError(err.message || 'Could not record consent. Please try again.')
    } finally { setSaving(false) }
  }

  const revoke = async () => {
    if (!recorded?.consentId || saving) return
    setError(null); setSaving(true)
    try {
      await api.post(`/ambient/consent/${recorded.consentId}/revoke`)
      setRevoked(true)
    } catch (err) {
      setError(err.message || 'Could not withdraw consent. Please try again.')
    } finally { setSaving(false) }
  }

  // Withdraw a past consent straight from the register.
  const revokeFromRegister = async (id) => {
    if (revokingId) return
    setRevokingId(id)
    try {
      await api.post(`/ambient/consent/${id}/revoke`)
      setRegister(rs => rs.map(c => (c.id === id ? { ...c, revoked_at: new Date().toISOString() } : c)))
    } catch { /* leave the row as-is; user can retry */ }
    finally { setRevokingId(null) }
  }

  const n = notice || FALLBACK_NOTICE

  return (
    <div style={s.wrap}>
      <div style={s.top}>
        <button style={s.back} onClick={onBack}>← Reflect</button>
      </div>

      <div style={s.head}>
        <div style={s.icon}>🤝</div>
        <h1 style={s.title}>Recording with others</h1>
        <p style={s.sub}>Before SOVRANOVA can ever sit in on a conversation, everyone in the room says yes — here, first.</p>
      </div>

      {!captureOn ? (
        <div style={s.notShipped}>
          Recording another person isn’t available yet. This step records consent only —
          capture opens in a later update, after legal review.
        </div>
      ) : (
        <div style={s.captureReady}>
          Recording with consent is enabled on this account. Everyone named signs and
          consents below — then recording can begin, and any participant can stop it at any time.
        </div>
      )}

      {step === 'participants' && (
        <div style={s.card}>
          <p style={s.label}>Who would be in the room?</p>
          <p style={s.hint}>Name each person who would be recorded, besides you. Everyone named must consent.</p>
          {people.map((p, i) => (
            <div key={i} style={s.personRow}>
              <input
                style={s.nameInput}
                value={p.name}
                onChange={e => setPerson(i, { name: e.target.value })}
                placeholder="Their name"
                maxLength={120}
              />
              <select style={s.roleSelect} value={p.role} onChange={e => setPerson(i, { role: e.target.value })}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
              {people.length > 1 && (
                <button style={s.remove} onClick={() => removePerson(i)} aria-label="Remove participant">✕</button>
              )}
            </div>
          ))}
          <button style={s.ghost} onClick={addPerson}>+ Add another person</button>
          <div style={s.controls}>
            <button style={s.primary} onClick={() => setStep('notice')} disabled={!allNamed}>Continue</button>
          </div>
        </div>
      )}

      {step === 'participants' && register.length > 0 && (
        <div style={s.card}>
          <p style={s.label}>Your consent records</p>
          <p style={s.hint}>Every consent stays visible here — and anyone can withdraw one at any time.</p>
          {register.map(c => {
            const names = (c.participants || []).map(p => p.name).filter(Boolean).join(', ') || '—'
            const when = c.consented_at ? new Date(c.consented_at).toLocaleDateString() : ''
            const withdrawn = !!c.revoked_at
            return (
              <div key={c.id} style={s.registerRow}>
                <span style={s.registerInfo}>
                  <span style={s.registerNames}>{names}</span>
                  <span style={s.registerMeta}>{when}{withdrawn ? ' · withdrawn' : c.all_party ? ' · active' : ' · incomplete'}</span>
                </span>
                {!withdrawn && (
                  <button style={s.registerRevoke} onClick={() => revokeFromRegister(c.id)} disabled={revokingId === c.id}>
                    {revokingId === c.id ? 'Withdrawing…' : 'Withdraw'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {step === 'notice' && (
        <div style={s.card}>
          <p style={s.label}>{n.title}{n.version ? ` · ${n.version}` : ''}</p>
          <p style={s.noticeIntro}>{n.intro}</p>
          <ul style={s.noticeList}>
            {n.points.map((pt, i) => <li key={i} style={s.noticePoint}>{pt}</li>)}
          </ul>
          <p style={s.hint}>Read this out loud (or hand over the phone) so every participant knows exactly what they’re agreeing to.</p>
          <div style={s.controls}>
            <button style={s.ghost} onClick={() => setStep('participants')}>Back</button>
            <button style={s.primary} onClick={() => setStep('consent')}>Everyone has read this</button>
          </div>
        </div>
      )}

      {step === 'consent' && (
        <div style={s.card}>
          <p style={s.label}>Each person signs, then consents</p>
          <p style={s.hint}>“{n.affirmation}” — each named person signs their own box, then taps their own button. Hand the phone across for each signature.</p>
          {named.map((p, i) => (
            <div key={i} style={s.signBlock}>
              <p style={s.signName}>{p.name.trim()} · {ROLES.find(r => r.value === p.role)?.label || 'Other'}</p>
              <SignaturePad
                value={p.signature}
                onChange={(sig) => setPerson(people.indexOf(p), { signature: sig, ...(sig ? {} : { consented: false }) })}
              />
              <button
                style={{ ...s.consentRow, ...(p.consented ? s.consentRowOn : {}), ...(!p.signature ? s.consentRowDisabled : {}) }}
                disabled={!p.signature}
                onClick={() => setPerson(people.indexOf(p), { consented: !p.consented })}>
                <span style={s.consentName}>{p.consented ? 'Consent given' : 'I consent'}</span>
                <span style={p.consented ? s.consentYes : s.consentNo}>
                  {p.consented ? '✓ Consented' : p.signature ? 'Tap to consent' : 'Sign first'}
                </span>
              </button>
            </div>
          ))}
          {error && <p style={s.error}>{error}</p>}
          <div style={s.controls}>
            <button style={s.ghost} onClick={() => setStep('notice')}>Back</button>
            <button style={s.primary} onClick={submit} disabled={!allConsented || saving}>
              {saving ? 'Recording consent…' : 'Record consent'}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div style={s.card}>
          {!revoked ? (
            <>
              <p style={s.doneMark}>✓</p>
              <p style={s.doneTitle}>Consent recorded</p>
              <p style={s.hint}>
                {named.map(p => p.name.trim()).join(', ')} consented under notice {n.version || 'current'}.
                It’s logged in your private consent record — and any participant can withdraw it at any time.
              </p>
              {!captureOn && (
                <div style={s.notShipped}>
                  ⚠ Live recording is NOT enabled on this account, so nothing will record today —
                  this consent is stored for when it is. Do not use the Reflect screen's own
                  microphone to record another person; it is for your own voice only.
                </div>
              )}
              {error && <p style={s.error}>{error}</p>}
              <div style={s.controls}>
                <button style={s.ghost} onClick={revoke} disabled={saving}>{saving ? 'Withdrawing…' : 'Withdraw consent'}</button>
                {captureOn && recorded?.allParty && typeof onBeginCapture === 'function' ? (
                  <button
                    style={s.primary}
                    onClick={() => onBeginCapture(recorded.consentId, named.map(p => p.name.trim()))}>
                    ● Begin recording
                  </button>
                ) : (
                  <button style={s.primary} onClick={onBack}>Done</button>
                )}
              </div>
            </>
          ) : (
            <>
              <p style={s.doneTitle}>Consent withdrawn</p>
              <p style={s.hint}>This consent is revoked. If a recording had existed, it would stop and be scheduled for deletion.</p>
              <div style={s.controls}>
                <button style={s.primary} onClick={onBack}>Done</button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

const s = {
  wrap:      { minHeight:'100vh', background:'linear-gradient(165deg,#161922,#0e1119)', color:'#e8eef4', padding:'1.1rem 1.1rem 3rem', maxWidth:640, margin:'0 auto' },
  top:       { display:'flex', justifyContent:'flex-start' },
  back:      { background:'transparent', border:'none', color:'rgba(240,244,248,.6)', fontSize:'0.9rem', cursor:'pointer', padding:'0.4rem 0' },
  head:      { textAlign:'center', padding:'0.6rem 0 1rem' },
  icon:      { fontSize:'2.2rem' },
  title:     { margin:'0.3rem 0 0.4rem', fontSize:'1.5rem', fontWeight:800, letterSpacing:'0.02em', color:'#f0f4f8' },
  sub:       { margin:0, fontSize:'0.9rem', lineHeight:1.55, color:'rgba(240,244,248,.55)' },
  notShipped:{ background:'rgba(250,204,21,.08)', border:'1px solid rgba(250,204,21,.3)', borderRadius:'0.7rem', color:'#fde68a', fontSize:'0.8rem', lineHeight:1.5, padding:'0.6rem 0.8rem', marginBottom:'0.6rem' },
  captureReady:{ background:'rgba(52,211,153,.08)', border:'1px solid rgba(52,211,153,.3)', borderRadius:'0.7rem', color:'#a7f3d0', fontSize:'0.8rem', lineHeight:1.5, padding:'0.6rem 0.8rem', marginBottom:'0.6rem' },
  signBlock: { background:'rgba(0,0,0,.18)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'0.8rem', padding:'0.7rem 0.8rem', marginBottom:'0.7rem' },
  signName:  { fontSize:'0.9rem', fontWeight:700, color:'#f0f4f8', margin:'0 0 0.2rem' },
  consentRowDisabled:{ opacity:.45, cursor:'not-allowed' },
  card:      { background:'rgba(255,255,255,.04)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'1rem', padding:'1.1rem', marginTop:'0.6rem' },
  label:     { fontSize:'0.64rem', letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(240,244,248,.4)', fontWeight:700, margin:'0 0 0.4rem' },
  hint:      { fontSize:'0.84rem', lineHeight:1.55, color:'rgba(240,244,248,.55)', margin:'0 0 0.8rem' },
  personRow: { display:'flex', gap:'0.5rem', marginBottom:'0.55rem', alignItems:'center' },
  nameInput: { flex:'1 1 auto', minWidth:0, background:'rgba(0,0,0,.25)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'0.6rem', color:'#f0f4f8', fontSize:'0.9rem', padding:'0.65rem 0.7rem', fontFamily:'inherit' },
  roleSelect:{ background:'rgba(0,0,0,.35)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'0.6rem', color:'#f0f4f8', fontSize:'0.85rem', padding:'0.6rem 0.4rem' },
  remove:    { background:'transparent', border:'none', color:'rgba(240,244,248,.4)', fontSize:'0.95rem', cursor:'pointer', padding:'0.3rem' },
  ghost:     { flex:'1 1 auto', background:'transparent', border:'1px solid rgba(255,255,255,.18)', color:'#e8eef4', fontSize:'0.9rem', fontWeight:600, borderRadius:'0.7rem', padding:'0.75rem 1rem', cursor:'pointer' },
  primary:   { flex:'1 1 auto', background:'#8b5cf6', border:'none', color:'#fff', fontSize:'0.9rem', fontWeight:700, borderRadius:'0.7rem', padding:'0.75rem 1rem', cursor:'pointer' },
  controls:  { display:'flex', gap:'0.6rem', marginTop:'0.9rem', flexWrap:'wrap' },
  noticeIntro:{ fontSize:'0.92rem', lineHeight:1.6, color:'#f4f1ff', margin:'0 0 0.7rem' },
  noticeList:{ margin:'0 0 0.8rem', paddingLeft:'1.1rem', display:'flex', flexDirection:'column', gap:'0.45rem' },
  noticePoint:{ fontSize:'0.86rem', lineHeight:1.55, color:'rgba(240,244,248,.8)' },
  consentRow:{ width:'100%', display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.6rem', background:'rgba(0,0,0,.25)', border:'1px solid rgba(255,255,255,.12)', borderRadius:'0.7rem', padding:'0.8rem 0.9rem', marginBottom:'0.55rem', cursor:'pointer', color:'#e8eef4', fontFamily:'inherit', textAlign:'left' },
  consentRowOn:{ background:'rgba(52,211,153,.1)', border:'1px solid rgba(52,211,153,.45)' },
  consentName:{ fontSize:'0.9rem', fontWeight:600 },
  consentYes:{ fontSize:'0.85rem', fontWeight:700, color:'#6ee7b7', whiteSpace:'nowrap' },
  consentNo: { fontSize:'0.85rem', color:'rgba(240,244,248,.5)', whiteSpace:'nowrap' },
  registerRow:{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:'0.6rem', background:'rgba(0,0,0,.2)', border:'1px solid rgba(255,255,255,.08)', borderRadius:'0.7rem', padding:'0.65rem 0.8rem', marginBottom:'0.5rem' },
  registerInfo:{ display:'flex', flexDirection:'column', gap:'0.15rem', minWidth:0 },
  registerNames:{ fontSize:'0.88rem', fontWeight:600, color:'#f0f4f8', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  registerMeta:{ fontSize:'0.74rem', color:'rgba(240,244,248,.45)' },
  registerRevoke:{ background:'transparent', border:'1px solid rgba(255,255,255,.2)', color:'rgba(240,244,248,.75)', fontSize:'0.78rem', fontWeight:600, borderRadius:'0.55rem', padding:'0.45rem 0.7rem', cursor:'pointer', whiteSpace:'nowrap' },
  doneMark:  { fontSize:'2rem', color:'#6ee7b7', textAlign:'center', margin:'0 0 0.2rem' },
  doneTitle: { fontSize:'1.1rem', fontWeight:700, textAlign:'center', margin:'0 0 0.6rem', color:'#f0f4f8' },
  error:     { color:'#ffb4b4', fontSize:'0.82rem', margin:'0.7rem 0 0' },
}
